import { Navigate, useNavigate } from 'react-router-dom';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { NavBar } from '@design-system/NavBar.tsx';
import { ACCOUNT_MANAGEMENT_PATH, SMP_PATH } from '@/constants/paths';
import { useSMPData } from './hooks/useSMPData';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { SMPLandingPageSkeleton } from './SMPLandingPageSkeleton';
import { SMPMaintenanceBanner } from './components';
import { SMPSubscriptionDetailSummary } from './sections/SMPSubscriptionDetailSummary';
import { SMPLandingPageOrderInProgress } from './sections/SMPLandingPageOrderInProgress';
import { SMPLandingPageShippingPayment } from './sections/SMPLandingPageShippingPayment';
import { SMPSubscriptionDetailQuickActions } from './sections/SMPSubscriptionDetailQuickActions';
import { isComponentizationEligible } from './utils/smpHelpers';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';

export const SMPSubscriptionDetailPage = () => {
  const navigate = useNavigate();
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const { withSubId } = useSMPNavigate();
  const {
    subscriptionDetail,
    customerDiscount,
    customerDiscounts,
    hasOrderInProgress,
    showMaintenance,
    isLoading,
    isError,
    isInvalidSubscription,
  } = useSMPData({ screenName: 'SMPSubscriptionDetailPage' });

  if (isLoading) {
    return <SMPLandingPageSkeleton />;
  }

  if (isInvalidSubscription) {
    return <Navigate to={ACCOUNT_MANAGEMENT_PATH} replace />;
  }

  if (isError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  // Safety net: a non-componentized subscription reached this route (stale
  // link, bookmark, direct nav) — bounce to the regular SMP flow instead of
  // rendering the componentized page for it. Prefer the BE-driven
  // `componentization` flag from the detail API; fall back to the SKU
  // allowlist. Also bounce when the enable_componentization kill switch is off.
  // See SMP_COMPONENTIZATION_TECH_SPEC_v1.md §7 Open Question 5.
  if (
    !isComponentizationEnabled ||
    !isComponentizationEligible(
      subscriptionDetail.componentization,
      subscriptionDetail.line_items,
      eligibleSkus,
    )
  ) {
    return <Navigate to={withSubId(SMP_PATH)} replace />;
  }

  const blurredClass = showMaintenance
    ? 'blur-sm pointer-events-none select-none'
    : '';

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-detailPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          data-testid="navbar"
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={() => navigate(ACCOUNT_MANAGEMENT_PATH)}
        />

        {showMaintenance && <SMPMaintenanceBanner />}

        <h1
          className="text-[32px] md:text-[40px] font-normal text-primary"
          data-testid="smp-detail-pageTitle"
        >
          My Subscriptions
        </h1>

        <div className={blurredClass}>
          <SMPSubscriptionDetailSummary
            subscription={subscriptionDetail}
            hasOrderInProgress={hasOrderInProgress}
            customerDiscount={customerDiscount}
            customerDiscounts={customerDiscounts}
          />
        </div>

        {hasOrderInProgress && (
          <div className={blurredClass}>
            <SMPLandingPageOrderInProgress />
          </div>
        )}
      </div>

      <div className={blurredClass}>
        <SMPLandingPageShippingPayment
          subscription={subscriptionDetail}
          isComponentization={true}
        />
      </div>

      {!showMaintenance && (
        <SMPSubscriptionDetailQuickActions
          subscriptionStatus={subscriptionDetail.subscription_status}
        />
      )}
    </section>
  );
};
